/* Filter drawer UX (BJP-38).
 *
 * The drawer shell is the global pd-drawer primitive (<dialog class="pd-drawer">
 * controlled by PDDrawer). PDDrawer handles open/close animation, backdrop
 * click, ESC, focus trap, and body scroll lock — we just layer the
 * facet-specific behaviour on top:
 *
 * 1. Mobile deferred apply + cancel-on-close. On desktop, facets.js's
 *    debounced `input` listener fires on every checkbox/radio change and
 *    refreshes the grid live ("Desktop: selecting a filter updates the
 *    product list straight away"). On mobile, we intercept the bubbling
 *    `input` events with `capture: true`, snapshot all `name="filter.*"`
 *    checkboxes and the `name="sort_by"` radio when the drawer opens, and:
 *      - Apply Filters button → release the interceptor, dispatch one
 *        final `input` so facets.js submits, drop the snapshot (the pending
 *        selection is the committed state now), then close.
 *      - X / View all results / ESC / backdrop click before Apply → restore
 *        the snapshot to all inputs (no events fired), then close. Server
 *        state is unchanged.
 *
 * 2. Size-region tabs (AU / US / UK). Mirrors the PDP pattern from
 *    `snippets/pd-product-info.liquid` — toggles `data-active-region` on
 *    the fieldset and `aria-pressed` on the tabs. Visual switch only for
 *    MVP; per-region label swap is a follow-up (BJP-50).
 */
(function () {
	var DESKTOP_MEDIA = '(min-width: 750px)';
	var DRAWER_ID = 'FilterDrawer';

	function isDesktop() {
		return window.matchMedia(DESKTOP_MEDIA).matches;
	}

	function getDialog() {
		return document.getElementById(DRAWER_ID);
	}

	function getForm() {
		return document.getElementById('FacetFiltersFormMobile');
	}

	function isDrawerOpen() {
		var dialog = getDialog();
		return Boolean(dialog && dialog.open);
	}

	function closeDrawer() {
		var dialog = getDialog();
		if (dialog && globalThis.PDDrawer) {
			globalThis.PDDrawer.for(dialog).close();
		}
	}

	// ── Apply-filter enabled state (BJP-38, #482, BJP-342) ──
	// The "Apply filter" button (both viewports) is disabled until there's
	// something to apply. There are three ways to have something:
	//
	//   1. ≥1 filter selected — a `filter.*` checkbox/radio checked, or a
	//      price-range input with a value.
	//   2. The shopper touched the sort group in this drawer session
	//      (BJP-342). Tracked as an interaction flag, NOT by comparing the
	//      checked radio against the one Liquid rendered `checked`. The
	//      comparison approach looks tidier but is wrong: on any collection
	//      the currently-applied sort IS the rendered default, so selecting
	//      it read as "no change" and greyed the button out — Best Selling
	//      on /collections/all, Featured on /collections/dresses, and so on.
	//      Which option was dead therefore varied per collection and was
	//      invisible to the shopper. It also disagreed with rule 1: a
	//      re-selected category enables the button, so a re-selected sort
	//      must too. Applying an unchanged sort is a harmless no-op —
	//      facets.js re-submits the same state and serves it from cache.
	//   3. #482: a sort-only drawer (Enable filtering off, Enable sorting on)
	//      renders no `filter.*` inputs at all — only the sort_by radios — so
	//      there's always a sort to commit and the button ships enabled.
	//
	// The flag resets every time the drawer opens and closes, so a fresh
	// open with nothing touched still ships disabled (BJP-38), and a cancel
	// close can't leave the button enabled over a reverted selection.
	var sortTouched = false;

	function hasSelectedFilter(form) {
		if (form.querySelector('input[name^="filter."]:checked')) return true;
		var priceInputs = form.querySelectorAll('input[name^="filter.v.price"]');
		for (var i = 0; i < priceInputs.length; i++) {
			if (priceInputs[i].value) return true;
		}
		// BJP-342: the shopper picked a sort in this session.
		if (sortTouched && form.querySelector('input[name="sort_by"]')) return true;
		// #482: sort-only drawer — no filter inputs, but a sort_by group exists.
		if (!form.querySelector('input[name^="filter."]') && form.querySelector('input[name="sort_by"]')) {
			return true;
		}
		return false;
	}

	function refreshApplyDisabled() {
		var form = getForm();
		if (!form) return;
		var enabled = hasSelectedFilter(form);
		form.querySelectorAll('.pd-facets-drawer__apply').forEach(function (btn) {
			btn.disabled = !enabled;
		});
	}

	// ── Size-scope inputs (BIL-143) ────────────────────────
	// The Clothing Size and Shoe Size accordions are two views of ONE Shopify
	// filter (`filter.v.option.size`), so a tick on its own cannot say which
	// half of the catalogue it meant. Liquid renders hidden `[data-pd-scope]`
	// inputs alongside each accordion; this keeps their `disabled` state in
	// step with what is ticked. Disabled inputs are not serialised, so an
	// untouched drawer still posts exactly what it did before BIL-143.
	//
	//   shoe         -> filter.p.product_type=shoes
	//   clothing     -> filter.p.product_type=<each non-shoe type>
	//   availability -> filter.v.availability=1
	//
	// The availability input is per-accordion and follows that accordion's own
	// selection, so at most one is ever live.
	function syncScopeInputs() {
		var form = getForm();
		if (!form) return;

		var ticked = { shoe: false, clothing: false };
		form.querySelectorAll('[data-pd-size-kind]').forEach(function (acc) {
			var kind = acc.getAttribute('data-pd-size-kind');
			if (!(kind in ticked)) return;
			if (acc.querySelector('input[type="checkbox"]:checked')) ticked[kind] = true;
		});

		form.querySelectorAll('[data-pd-scope]').forEach(function (input) {
			var scope = input.getAttribute('data-pd-scope');
			var live = false;
			if (scope === 'availability') {
				var acc = input.closest('[data-pd-size-kind]');
				var kind = acc && acc.getAttribute('data-pd-size-kind');
				live = kind ? !!ticked[kind] : ticked.shoe || ticked.clothing;
			} else {
				live = !!ticked[scope];
			}
			input.disabled = !live;
		});

		// Mutual exclusion. Both accordions write the same param, so mixing a
		// shoe size with a clothing size would apply one accordion's scope to
		// the other's values — "shoes in size 8 or 10" when the shopper asked
		// for shoe 8 and dress 10. Lock the opposite group while one is in use.
		//
		// Only boxes this function disabled are ever re-enabled: Liquid already
		// disables count-0 values, and `data-pd-suppressed` keeps the two sets
		// apart so a shopper can never be handed an option with no results.
		form.querySelectorAll('[data-pd-size-kind]').forEach(function (acc) {
			var kind = acc.getAttribute('data-pd-size-kind');
			var other = kind === 'shoe' ? 'clothing' : 'shoe';
			var suppress = !!ticked[other] && !ticked[kind];
			acc.querySelectorAll('input[type="checkbox"]').forEach(function (box) {
				var label = box.closest('label');
				if (suppress) {
					if (box.disabled) return;
					box.disabled = true;
					// `disabled` alone is announced by every screen reader, but it
					// also removes the box from the tab order — so state it
					// explicitly as well and let the note below carry the reason.
					box.setAttribute('aria-disabled', 'true');
					box.setAttribute('data-pd-suppressed', '');
					if (label) label.classList.add('pd-facets-drawer__option--disabled');
				} else if (box.hasAttribute('data-pd-suppressed')) {
					box.disabled = false;
					box.removeAttribute('aria-disabled');
					box.removeAttribute('data-pd-suppressed');
					if (label) label.classList.remove('pd-facets-drawer__option--disabled');
				}
			});

			// Explain the lock. Writing into the `aria-live` region announces it
			// at the moment it becomes true; the text then stays put so it is
			// still readable when the shopper browses back over the accordion.
			// Cleared to '' (not removed) so the element keeps its place in the
			// live region and CSS `:empty` collapses it.
			var note = acc.querySelector('[data-pd-size-note]');
			if (note) {
				var wanted = suppress
					? other === 'shoe'
						? 'Clear your shoe size selection to filter by clothing size.'
						: 'Clear your clothing size selection to filter by shoe size.'
					: '';
				if (note.textContent !== wanted) note.textContent = wanted;
			}
		});
	}

	// ── Scoped value verification (BIL-143) ────────────────
	// The two size accordions are split by VOCABULARY: a value lands in Shoe
	// Size because the shoe mapping set has a row for it. That is not the same
	// question as "does a shoe in THESE results actually come in that size" —
	// and Shopify cannot answer it from `filter.values`, whose counts are for
	// the whole result set, not for the accordion's scope.
	//
	// It shows up as dead options. `S/M` and `M/L` are real slipper sizes, so
	// they sit in the shoe mapping set; on /collections/shoes ticking S/M
	// returns 6 slippers, but on /collections/sale it returns 0 — there are no
	// S/M shoes on sale, only S/M clothing.
	//
	// Resolve it the way the colour facet already resolves its truncated list
	// on search: one Section Rendering request per accordion, asking Shopify
	// for this page scoped to that accordion's half of the catalogue. Whatever
	// comes back IS the authoritative value list; anything else is hidden.
	//
	// Only runs when both accordions are on screen — a single accordion has no
	// competing vocabulary to disambiguate. Keyed on the non-size filter
	// context so ticking sizes doesn't refetch, and it fails open: any network
	// or parse problem leaves every value visible, exactly as before.
	var scopeVerifyKey = null;
	// The probe is lazy: nothing fires until the shopper actually opens the
	// drawer. That has to be an explicit flag rather than "first call wins",
	// because on SEARCH the colour top-up script mutates the form during page
	// load, which trips the MutationObserver below and would otherwise buy two
	// probe requests for every search that never touches the filters.
	var drawerEverOpened = false;
	var scopeVerifyInFlight = 0;

	// Which size values exist depends only on the OTHER filters plus the search
	// term — not on sort order, pagination, or the `options[prefix]` Shopify
	// appends to a search submit. Signing the whole query string re-probed on
	// every interaction (measured: three extra requests per tick, and climbing);
	// signing just the determining params makes a size tick a no-op and a real
	// context change — a category, a colour, a new search — the only trigger.
	var SCOPE_OWNED = ['filter.v.option.size', 'filter.p.product_type', 'filter.v.availability'];

	function contextSignature() {
		var params = new URLSearchParams(window.location.search);
		var parts = [];
		params.forEach(function (value, key) {
			if (SCOPE_OWNED.indexOf(key) !== -1) return;
			if (key !== 'q' && key.indexOf('filter.') !== 0) return;
			parts.push(key + '=' + value);
		});
		return parts.sort().join('&');
	}

	// The probe result is cached per context and RE-APPLIED on every call.
	// facets.js replaces an accordion's innerHTML on each apply, which wipes the
	// hidden rows. Applying the result only once meant unticking a shoe size
	// brought back every clothing value the probe had removed, so the list came
	// back LONGER than it started — and the signature guard suppressed the
	// refetch that would have corrected it.
	var scopeVerifyAllowed = null;

	function applyScopeVerification() {
		if (!scopeVerifyAllowed) return;
		var form = getForm();
		if (!form) return;
		form.querySelectorAll('[data-pd-size-kind]').forEach(function (acc) {
			var allowed = scopeVerifyAllowed[acc.getAttribute('data-pd-size-kind')];
			if (!allowed) return;
			var visible = 0;
			acc.querySelectorAll('input[type="checkbox"]').forEach(function (box) {
				var row = box.closest('li');
				if (!row) return;
				// A value the shopper has applied always stays, so the control that
				// produced the current page can always be undone.
				var keep = allowed[box.value] === true || box.checked;
				row.hidden = !keep;
				if (keep) visible++;
			});
			acc.hidden = visible === 0;
		});
	}

	function verifyScopedSizeValues() {
		if (!drawerEverOpened) return;
		var form = getForm();
		if (!form || typeof fetch !== 'function') return;
		var accs = form.querySelectorAll('[data-pd-size-kind]');
		if (accs.length < 2) return;

		// Same section lookup facets.js uses for its own re-renders.
		var grid = document.getElementById('product-grid');
		var sectionId = grid && grid.dataset ? grid.dataset.id : null;
		if (!sectionId) return;

		var signature = contextSignature();
		if (signature === scopeVerifyKey) {
			// Same context, so no refetch — but the accordions may have been
			// re-rendered since, which resets the hidden rows. Put them back.
			applyScopeVerification();
			return;
		}
		if (scopeVerifyInFlight) return;
		scopeVerifyKey = signature;
		scopeVerifyAllowed = null;
		// A single apply fires the observer several times as facets.js swaps
		// each accordion in turn. Without this, each of those intermediate
		// states starts its own round of probes.
		scopeVerifyInFlight = accs.length;

		Array.prototype.forEach.call(accs, function (acc) {
			var kind = acc.getAttribute('data-pd-size-kind');
			var params = new URLSearchParams(window.location.search);
			['filter.v.option.size', 'filter.p.product_type', 'filter.v.availability', 'page'].forEach(
				function (k) {
					params.delete(k);
				},
			);
			// Read the scope straight off the hidden inputs Liquid rendered for
			// this accordion, so there is one definition of "shoes" / "not shoes".
			acc.querySelectorAll('[data-pd-scope]').forEach(function (input) {
				if (input.getAttribute('data-pd-scope') === 'availability') return;
				params.append(input.name, input.value);
			});
			params.set('filter.v.availability', '1');
			params.set('section_id', sectionId);

			fetch(window.location.pathname + '?' + params.toString())
				.then(function (response) {
					if (!response.ok) throw new Error('scope probe failed');
					return response.text();
				})
				.then(function (html) {
					var doc = new DOMParser().parseFromString(html, 'text/html');
					var source = doc.querySelector('[data-pd-size-kind="' + kind + '"]');
					if (!source) return;

					var allowed = Object.create(null);
					var found = 0;
					source.querySelectorAll('input[type="checkbox"]:not(:disabled)').forEach(function (box) {
						allowed[box.value] = true;
						found++;
					});
					// Nothing came back — treat as inconclusive rather than
					// blanking the accordion.
					if (!found) return;

					if (!scopeVerifyAllowed) scopeVerifyAllowed = {};
					scopeVerifyAllowed[kind] = allowed;
					applyScopeVerification();
				})
				.catch(function () {
					// Fail open — leave the unverified list exactly as rendered.
				})
				.then(function () {
					if (scopeVerifyInFlight > 0) scopeVerifyInFlight--;
				});
		});
	}

	// ── Active-filter count indicator (BJP-38, Jae) ────────
	// Mirror the number of applied filters into the header badges (drawer
	// header + the banner Filter / Filter & Sort by triggers, all marked
	// `[data-pd-filter-count]`). The applied chips are the source of truth —
	// facets.js re-renders them on every committed change, so counting them
	// keeps the badges right on both viewports (mobile reflects the committed
	// state, not the pending checkboxes). `[hidden]` collapses a 0 badge.
	function refreshFilterCount() {
		var form = getForm();
		var count = form ? form.querySelectorAll('.pd-facets-drawer__chip').length : 0;
		var text = String(count);
		var hide = count === 0;
		document.querySelectorAll('[data-pd-filter-count]').forEach(function (badge) {
			// The drawer-header badge lives inside the observed form, so only
			// write when the value actually changes — otherwise setting
			// textContent would emit a childList mutation and re-trigger the
			// MutationObserver in an endless loop.
			if (badge.textContent !== text) badge.textContent = text;
			if (badge.hidden !== hide) badge.hidden = hide;
		});
	}

	// ── Mobile defer/cancel state ──────────────────────────
	// Snapshot of every filter input when the drawer opens. Shape:
	// { [id]: { el: HTMLInputElement, checked: boolean }, ... }
	var snapshot = null;

	function snapshotInputs(form) {
		var snap = {};
		form.querySelectorAll('input[name^="filter."], input[name="sort_by"]').forEach(function (input) {
			snap[input.id || input.name + ':' + input.value] = {
				el: input,
				checked: input.checked,
			};
		});
		return snap;
	}

	function restoreSnapshot(snap) {
		if (!snap) return;
		Object.values(snap).forEach(function (entry) {
			if (entry.el && document.contains(entry.el)) {
				entry.el.checked = entry.checked;
			}
		});
	}

	// On mobile only, swallow `input` events before facets.js's bubble
	// listener runs (we capture so we fire first). The `applyingNow` flag
	// lets the Apply path pass one synthetic event through, and marks the
	// close that follows as "this is a commit, not a cancel" so the
	// `pd-drawer:closed` handler below knows not to revert (BJP-342).
	var applyingNow = false;
	var applyingReleaseTimer = null;

	// PDDrawer's close is animated — `pd-drawer:closed` only fires after the
	// panel finishes sliding out (transitionend, or a 400ms fallback). If the
	// dialog was already closed, `PDDrawer.close()` early-returns and the
	// event never arrives at all, so the flag also needs a timer to fall back
	// on — otherwise it sticks `true` and the mobile interceptor stops
	// deferring every later change.
	var APPLY_RELEASE_FALLBACK_MS = 600;

	function releaseApplying() {
		applyingNow = false;
		if (applyingReleaseTimer) {
			clearTimeout(applyingReleaseTimer);
			applyingReleaseTimer = null;
		}
	}

	function captureInputEvent(event) {
		var target = event.target;
		if (!(target instanceof Element)) return;
		if (!target.closest('#' + DRAWER_ID)) return;
		// Rule 2 of hasSelectedFilter: remember that the sort group was used.
		// A radio only fires `input` when the value actually moves, so this
		// can't be set by re-clicking the already-checked option — which is
		// correct, that genuinely changes nothing.
		if (target.getAttribute('name') === 'sort_by') sortTouched = true;
		// Keep the Apply filter button enabled only while ≥1 filter is selected
		// (Jae, BJP-38). Runs on both viewports, before the mobile stop below —
		// on mobile the checkbox state still toggles (we only stop the grid
		// refresh), so reading :checked here reflects the pending selection.
		refreshApplyDisabled();
		if (isDesktop()) return;
		if (applyingNow) return;
		if (!isDrawerOpen()) return;
		event.stopImmediatePropagation();
	}

	function commitChangesAndClose(form) {
		applyingNow = true;
		// facets.js debounces input by 500ms. Wait past the debounce window
		// before closing so the grid refresh fetch is already in flight by the
		// time the panel starts sliding out.
		form.dispatchEvent(new Event('input', { bubbles: true }));
		setTimeout(function () {
			// The pending selection IS the committed state now, so there is
			// nothing left to revert — drop the pre-open snapshot before the
			// close starts (BJP-342). Without this the `pd-drawer:closed`
			// handler used to restore it and un-check the sort the shopper
			// had just applied. Filter checkboxes hid the bug: facets.js
			// re-renders every `.js-filter` block from the server response,
			// so their snapshot entries point at detached nodes and
			// `restoreSnapshot`'s `document.contains` guard skips them. The
			// sort accordion carries no `js-filter` class, is never
			// re-rendered, and so took the revert.
			snapshot = null;
			closeDrawer();
			// Leave `applyingNow` set until `pd-drawer:closed` fires (see
			// `releaseApplying`) — clearing it here is what made the close
			// look like a cancel.
			applyingReleaseTimer = setTimeout(releaseApplying, APPLY_RELEASE_FALLBACK_MS);
		}, 510);
	}

	// ── Drawer lifecycle hooks (pd-drawer events) ──────────
	// PDDrawer fires `pd-drawer:opened` after the panel finishes sliding in
	// and `pd-drawer:closed` after it finishes sliding out. We snapshot on
	// open and revert (mobile only) on close — unless commitChangesAndClose
	// has set `applyingNow`, in which case the snapshot is stale by design.
	document.addEventListener('pd-drawer:opened', function (event) {
		if (event.target.id !== DRAWER_ID) return;
		var form = getForm();
		if (form) snapshot = snapshotInputs(form);
		// Fresh session — nothing touched yet, so a plain open still ships the
		// button disabled when no filter is selected (BJP-38).
		sortTouched = false;
		refreshApplyDisabled();
		refreshFilterCount();
	});

	document.addEventListener('pd-drawer:closed', function (event) {
		if (event.target.id !== DRAWER_ID) return;
		if (!applyingNow && !isDesktop()) {
			restoreSnapshot(snapshot);
		}
		snapshot = null;
		// The commit is finished — every later close is a cancel again.
		releaseApplying();
		// Drop the sort-interaction flag too: on a cancel close the radios have
		// just been reverted, so leaving it set would show an enabled button
		// over a selection the shopper backed out of.
		sortTouched = false;
		// Reflect the restored selection so the button is correct on next open.
		refreshApplyDisabled();
	});

	// ── Event delegation ───────────────────────────────────

	// Capture-phase input listener — runs before facets.js so we can stop
	// it on mobile.
	document.addEventListener('input', captureInputEvent, true);

	// Apply Filters (mobile only) — commit pending changes then close.
	document.addEventListener('click', function (event) {
		var target = event.target;
		if (!(target instanceof Element)) return;
		var apply = target.closest('[data-pd-apply-filters]');
		if (!apply) return;
		event.preventDefault();
		var form = apply.closest('form');
		if (!form) return;
		commitChangesAndClose(form);
	});

	// "Clear filters" commits the sort the shopper can SEE (BJP-342).
	// The clear link is a `facet-remove` anchor, so facets.js reads its href's
	// query string and re-fetches the grid from it — whatever sort the href
	// holds becomes the applied sort. Liquid stamps the *applied* sort into that
	// href at render time (`results_url` in snippets/facets.liquid), which is
	// right on a fresh open. But the mobile drawer defers sort changes until
	// Apply, while Clear filters fires immediately — so a shopper who picks a
	// sort and then hits Clear would commit the previous one while the radio on
	// screen shows the new one. Rewriting the href from the checked radio keeps
	// the link and the visible selection in agreement on both viewports (on
	// desktop facets.js `syncSortInputs` mirrors the banner <select> into these
	// same radios, so the read is valid there too).
	//
	// Capture phase so this runs before FacetRemove's own click listener, which
	// is attached to the anchor itself. `link.href` (the browser-resolved
	// absolute URL) rather than the raw attribute: on search the href starts
	// with "?", so resolving against the origin alone would drop the "/search"
	// path and break a middle-click / no-JS navigation.
	document.addEventListener(
		'click',
		function (event) {
			var target = event.target;
			if (!(target instanceof Element)) return;
			var link = target.closest('.pd-facets-drawer__clear');
			if (!link) return;
			var form = getForm();
			// No sort group at all (Enable sorting off) — nothing to preserve.
			var checked = form && form.querySelector('input[name="sort_by"]:checked');
			if (!checked) return;
			var url = new URL(link.href);
			url.searchParams.set('sort_by', checked.value);
			link.setAttribute('href', url.pathname + url.search);
			// Mirror the committed value into the banner <select> as well. A
			// clear-click goes through facets.js `onActiveFilterClick`, which
			// never calls `syncSortInputs` — so without this the banner control
			// would keep displaying the previous sort. Unreachable for a shopper
			// today (the pending state is mobile-only and the banner sort is
			// display:none below 750px), but it costs three lines and stops the
			// mismatch becoming real if that breakpoint ever moves.
			// `FacetFiltersForm` is a classic-script class declaration, so it is
			// a global lexical binding and NOT a property of `window` — hence
			// `typeof`, not `globalThis.FacetFiltersForm`.
			if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.syncSortInputs) {
				FacetFiltersForm.syncSortInputs(form);
			}
		},
		true,
	);

	// BIL-143 — verify on first open rather than on page load: the drawer is
	// rendered but hidden, so a shopper who never opens it never pays for the
	// probe requests.
	document.addEventListener(
		'click',
		function (event) {
			var target = event.target;
			if (!(target instanceof Element)) return;
			if (!target.closest('[data-pd-open-filter-drawer]')) return;
			drawerEverOpened = true;
			verifyScopedSizeValues();
		},
		true,
	);

	// Size region tabs — flip data-active-region + aria-pressed.
	// Capture-phase listener + stopImmediatePropagation: clicking a tab
	// inside the size accordion was still collapsing the surrounding
	// <details> because some bubble-phase listener was closing it.
	// Running before every other click handler short-circuits all of them,
	// and the explicit re-open at the bottom is defence-in-depth so the
	// accordion can't end up closed even if a listener does fire first.
	document.addEventListener(
		'click',
		function (event) {
			var target = event.target;
			if (!(target instanceof Element)) return;
			var tab = target.closest('[data-pd-region-tab]');
			if (!tab) return;
			event.preventDefault();
			event.stopImmediatePropagation();
			var fieldset = tab.closest('[data-pd-size-regions]');
			if (!fieldset) return;
			var region = tab.getAttribute('data-pd-region-tab');
			fieldset.setAttribute('data-active-region', region);
			fieldset.querySelectorAll('[data-pd-region-tab]').forEach(function (t) {
				var pressed = t.getAttribute('data-pd-region-tab') === region;
				t.setAttribute('aria-pressed', pressed ? 'true' : 'false');
				t.classList.toggle('is-active', pressed);
			});
			if (fieldset.tagName === 'DETAILS' && !fieldset.open) {
				fieldset.open = true;
			}
		},
		true,
	);

	// BIL-143 — recompute the scope inputs before facets.js serialises the form.
	// facets.js binds a debounced `input` listener on the form (bubble phase);
	// a capture-phase listener here always runs first, so the hidden inputs are
	// already correct by the time the query is built. `change` is belt-and-braces
	// for anything that sets `.checked` without an `input` event.
	['input', 'change'].forEach(function (type) {
		document.addEventListener(
			type,
			function (event) {
				var target = event.target;
				if (!(target instanceof Element)) return;
				if (!target.closest('[data-pd-size-kind]')) return;
				syncScopeInputs();
			},
			true,
		);
	});

	// Initial state on load — drawer renders closed; reflects the server-side
	// applied filters (the buttons also ship `disabled` from Liquid when
	// applied_count == 0, this just keeps JS and markup in sync).
	refreshApplyDisabled();
	refreshFilterCount();
	syncScopeInputs();

	// Recompute after facets.js re-renders. Removing a chip or hitting "Clear
	// filters" makes facets.js replace the `.js-filter` and
	// `.pd-facets-drawer__applied` innerHTML (it unchecks the boxes / updates
	// the chips) without firing an `input` event and without touching the
	// footer button or the header badges — so the Apply button would otherwise
	// stay enabled, and the count badges would go stale, after the chips
	// change (BJP-38). Watch the form for those re-renders and recompute both.
	// childList/subtree only: we don't observe attributes, so toggling the
	// button's own `disabled`/the badge text here can't retrigger the observer.
	(function observeReRenders() {
		var form = getForm();
		if (!form || typeof MutationObserver === 'undefined') return;
		new MutationObserver(function () {
			refreshApplyDisabled();
			refreshFilterCount();
			// BIL-143: a re-render replaces the accordions' innerHTML, which
			// restores the hidden scope inputs to their Liquid defaults and
			// drops the `data-pd-suppressed` markers. Recompute from the
			// freshly rendered checkboxes.
			syncScopeInputs();
			// Re-verify only when the non-size filter context actually moved
			// (a category or colour changes which sizes exist); the signature
			// check inside makes a size tick a no-op.
			verifyScopedSizeValues();
		}).observe(form, { childList: true, subtree: true });
	})();
})();
